import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isNull,
  omitBy,
  isNil,
  omit,
  isEmpty,
  pick,
  isUndefined,
} from "lodash";
import {
  AlertBox,
  PhoneInput,
  SelectInput,
  TextInput,
  Button,
} from "@life-line-lab/components-react";
import errorHandler from "Utils/error-handler";
import { RecipientInfoSchema } from "./recipient-info-validation";
import useStatesList from "Hooks/states-option.hook";
import { Col, Row } from "antd";
import useOrderFormStates from "../../order-form-states";

interface RecipientInfo {
  info?: any;
  onPrev?: any;
  onNext?: any;
  checkPostCode?: any;
  countryOption?: any;
  doesPostcodeExist?: any;
  parentInput?: any;
  handleCoordinatesChecking: (address: any) => any;
}

const initialInput = {
  recipientName: "",
  recipientEmail: "",
  contactNo: {
    dialCode: "60",
    number: "",
  },
  street1: "",
  street2: "",
  postcode: "",
  city: "",
  state: null,
  country: "MY",
  lon: "",
  lat: "",
};

const RecipientInfo: React.FC<RecipientInfo> = ({
  doesPostcodeExist,
  onPrev,
  onNext,
  countryOption,
  parentInput,
  handleCoordinatesChecking,
}) => {
  const { t } = useTranslation();
  const initialError = {};
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const stateOption = useStatesList(input.country);
  const { isLoading } = useOrderFormStates();

  useEffect(() => {
    if (!isEmpty(parentInput)) {
      let filteredParentInput = pick(parentInput, [
        "recipientName",
        "recipientEmail",
      ]);
      setInput({
        ...initialInput,
        ...filteredParentInput,
        contactNo: parentInput.recipientContact,
        ...parentInput.recipientAddress,
        ...parentInput.receiverInfo?.address?.coordinate,
      });
    }
  }, [parentInput]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string | string[] | {}, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });

    if (name == "country") {
      setInput({ ...input, [name]: value as string, state: undefined });
      input.state = null;
    }
  };

  const handlePhoneChange = (
    value: { dialCode: string; number: string },
    name: string,
  ) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  // step 3
  //in handle submit call postcode set state, send to input.postcode

  const miniAddressNormaliser = (data) => {
    return omitBy(
      {
        street1: data.street1,
        street2: data.street2,
        city: data.city,
        state: data.state,
        postcode: data.postcode,
        country: data.country,
      },
      isUndefined,
    );
  };

  const handleSubmit = async () => {
    //check postcode sends input.postcode by checking with API
    //if fail show error
    //if success go to next stepper
    try {
      const valInput = {
        ...input,
        recipientEmail: input.recipientEmail.trim(),
      };

      const value: any = RecipientInfoSchema.validateSync(
        omitBy(valInput, isNull),
        {
          strict: false,
          abortEarly: true,
          stripUnknown: true,
        },
      );
      const addressToCheck = miniAddressNormaliser(input);

      let coordinatesObtained = {
        lat: value.lat,
        lon: value.lon,
      };

      if (!value.lon || !value.lat)
        coordinatesObtained = await handleCoordinatesChecking(addressToCheck);

      value.recipientContact = value.contactNo;
      value.recipientAddress = {
        street1: value.street1,
        street2: value.street2,
        postcode: value.postcode,
        city: value.city,
        state: value.state,
        country: value.country,
        coordinate: coordinatesObtained,
      };
      omit(value, [
        "street1",
        "street2",
        "postcode",
        "city",
        "state",
        "country",
        "contactNo",
      ]);

      onNext(value);
      // current > 0 ? onPrev() : current < steps.length - 1 ? onNext() : null;
      // info ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError((prev) => {
        return {
          ...prev,
          [path]: t(`${value}`, { field: t(`label.${path}`) }),
        };
      });
    }
  };

  return (
    <React.Fragment>
      {
        <div className="center header-title">
          {t("title.recipientInfo").toUpperCase()}
        </div>
      }
      <Row gutter={[15, 15]} className="mt-50">
        <Col xs={24} sm={24} md={12}>
          <Col xs={24} sm={24} className="mb-7">
            <TextInput
              isRequired={true}
              label={t("label.recipientName")}
              placeholder={t("txt.enterName")}
              value={input.recipientName}
              name="recipientName"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.recipientName} />
          </Col>
          <Col xs={24} sm={24} className="mb-7">
            <TextInput
              label={t("label.recipientEmail")}
              placeholder={t("txt.emailAddressExample")}
              value={input.recipientEmail}
              name="recipientEmail"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.recipientEmail} />
          </Col>
          <Col xs={24} sm={24} className="mb-7">
            <PhoneInput
              isRequired={true}
              isError={!!error.contactNo}
              label={t("label.recipientMobileNo")}
              name="contactNo"
              value={input.contactNo}
              placeholder={t("txt.enterMobileNo")}
              onChange={(value: any) => handlePhoneChange(value, "contactNo")}
            />
          </Col>
          <AlertBox
            text={error["contactNo.number"] || error["contactNo.dialCode"]}
          />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <label className="inp-label inp-label--required">
            {t("label.deliveryAddress")}
          </label>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.street1Example")}
              value={input.street1}
              name="street1"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.street1} />
          </div>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.street2Example")}
              value={input.street2}
              name="street2"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.street2} />
          </div>
          <Row gutter={[15, 15]}>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                isRequired={true}
                placeholder={t("txt.postcode")}
                value={input.postcode}
                name="postcode"
                onChange={handleFieldChange}
              />
              {doesPostcodeExist === false ? (
                <AlertBox text={t("error.postcodeExist")} />
              ) : (
                <AlertBox text={t(error.postcode)} />
              )}
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                isRequired={true}
                placeholder={t("txt.city")}
                value={input.city}
                name="city"
                onChange={handleFieldChange}
              />
              <AlertBox text={error.city} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <SelectInput
                showSearch
                options={stateOption}
                filterOption={(inputValue, option) =>
                  option.label
                    .toLowerCase()
                    .indexOf(inputValue.toLowerCase()) >= 0
                }
                value={input.state}
                isReadonly={isNil(input.country)}
                name="state"
                placeholder={t("option.state")}
                onChange={(value: string | string[]) =>
                  handleSelectChange(value, "state")
                }
              />
              <AlertBox text={error.state} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <SelectInput
                showSearch
                options={countryOption}
                filterOption={(inputValue, option) =>
                  option.label
                    .toLowerCase()
                    .indexOf(inputValue.toLowerCase()) >= 0
                }
                value={input.country}
                name="country"
                placeholder={t("txt.selectCountry")}
                onChange={(value: string | string[]) =>
                  handleSelectChange(value, "country")
                }
              />
              <AlertBox text={error.country} />
            </Col>
          </Row>
          <Row gutter={[15, 15]} className="mt-50">
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                label={`${t("label.longitude")}`}
                placeholder={t("label.longitude")}
                value={input.lon}
                name="lon"
                onChange={handleFieldChange}
              />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                label={`${t("label.latitude")}`}
                placeholder={t("label.latitude")}
                value={input.lat}
                name="lat"
                onChange={handleFieldChange}
              />
            </Col>
            <AlertBox text={error.lon} />
            <AlertBox text={error.lat} />
          </Row>
        </Col>
      </Row>
      <div className="steps-action center mt-50">
        <Button
          onClick={() => onPrev()}
          type="ghost"
          label={t("action.back")}
          className="mr-10"
          isDisabled={isLoading}
        />
        <Button
          type="primary"
          isLoading={isLoading}
          onClick={() => handleSubmit()}
          label={t("action.next")}
        />
      </div>
    </React.Fragment>
  );
};

export default RecipientInfo;
